import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import HeroBackground from './HeroBackground';
import { getApiBaseUrl } from '../../services/api';
import { loadDepartmentHeroImage } from '../../utils/departmentHeroImages';
import { usePageReady } from '../../context/PageReadyContext';

const DepartmentHero = ({ department, showBackButton = true }) => {
  const navigate = useNavigate();
  const { signalHeroReady } = usePageReady();
  const [localHeroSrc, setLocalHeroSrc] = useState(null);
  const [localHeroResolved, setLocalHeroResolved] = useState(Boolean(department?.hero_image_id));

  const apiHeroSrc = department?.hero_image_id
    ? `${getApiBaseUrl()}/images/${department.hero_image_id}`
    : null;

  useEffect(() => {
    if (apiHeroSrc) {
      setLocalHeroResolved(true);
      return undefined;
    }
    let cancelled = false;
    setLocalHeroResolved(false);
    (async () => {
      try {
        const src = await loadDepartmentHeroImage(department?.name);
        if (!cancelled) setLocalHeroSrc(src);
      } catch {
        // HeroBackground shows gradient-only if load fails
      } finally {
        if (!cancelled) setLocalHeroResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiHeroSrc, department?.name]);

  const backgroundImage = apiHeroSrc || localHeroSrc;

  useEffect(() => {
    if (backgroundImage) return;
    if (localHeroResolved) signalHeroReady();
  }, [backgroundImage, localHeroResolved, signalHeroReady]);
  const lightGradient =
    'linear-gradient(135deg, rgba(37, 99, 235, 0.3) 0%, rgba(59, 130, 246, 0.2) 50%, rgba(139, 92, 246, 0.1) 100%)';

  return (
    <Box
      sx={{
        position: 'relative',
        height: { xs: '50vh', sm: '60vh', md: '70vh' },
        minHeight: { xs: 300, sm: 400, md: 500 },
        overflow: 'hidden',
      }}
    >
      <HeroBackground
        src={backgroundImage}
        alt={department?.name || 'Department'}
        priority
        gradient={lightGradient}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            {showBackButton && (
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  color: 'white',
                  borderColor: 'white',
                  mb: 3,
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Back to Services
              </Button>
            )}
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                mb: 2,
              }}
            >
              {department?.name || 'Our Departments'}
            </Typography>
          </Box>
        </Container>
      </HeroBackground>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.3,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
};

export default DepartmentHero;
